import { Button, Icon, Text } from '@zillow/constellation-10';
import { IconChevronUpFilled } from '@zillow/constellation-icons';
import { useEffect, useState } from 'react';
import { styled } from '@/styled-system/jsx';

const IMAGE_CHANGE_INTERVAL_MS = 5000;

const InstagramStoryContainer = styled('div', {
  base: {
    position: 'relative',
    maxWidth: '400px',
    minWidth: '400px',
    maxHeight: '700px',
    minHeight: '700px',
    boxSizing: 'border-box',
    backgroundSize: 'cover',
    borderRadius: '5px',
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-end',
    alignItems: 'center',
    padding: '16px',
    '& span': {
      zIndex: 1,
    },
    '& span:nth-child(3)': {
      lineHeight: '18px',
    },
  },
});

const Backdrop = styled('div', {
  base: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    background: 'rgba(0, 0, 0, 0.5)',
    zIndex: 0,
  },
});

const StoryHeader = styled('div', {
  base: {
    position: 'absolute',
    padding: '8px',
    width: '100%',
    top: 0,
    left: 0,
    boxSizing: 'border-box',
    gap: '3px',
    display: 'flex',
  },
});

const StoryLine = styled('div', {
  base: {
    flexGrow: 1,
    height: '2px',
    backgroundColor: 'rgba(255, 255, 255, 0.4)',
    position: 'relative',
    '&::after': {
      content: '""',
      display: 'block',
      position: 'absolute',
      top: 0,
      left: 0,
      height: '2px',
      width: 0,
      transition: `width ${IMAGE_CHANGE_INTERVAL_MS}ms linear`,
    },
  },
  variants: {
    active: {
      true: {
        '&::after': {
          width: '100%',
          backgroundColor: 'white',
        },
      },
    },
    finished: {
      true: {
        backgroundColor: 'white',
        transition: 'none',
      },
    },
  },
});

const LearnMoreButton = styled(Button, {
  base: {
    borderRadius: '20px',
    '&, &:focus, &:active, &:hover': {
      backgroundColor: 'white !important',
      color: 'text.dark !important',
      border: 0,
      boxShadow: 'none',
    },
  },
});

export const InstagramStory = ({
  description,
  imageUrls,
  title,
}: {
  imageUrls: Array<string>;
  description: string;
  title: string;
}) => {
  const [activeIndex, setActiveIndex] = useState(-1);

  useEffect(() => {
    // need to start at -1 and then set to 0 to trigger the initial transition
    // of the story line otherwise it will not happen
    setActiveIndex(0);
  }, []);

  useEffect(() => {
    const timeout = setTimeout(() => {
      setActiveIndex((activeIndex + 1) % imageUrls.length);
    }, IMAGE_CHANGE_INTERVAL_MS);

    return () => clearTimeout(timeout);
  }, [activeIndex, setActiveIndex]);

  const imageUrl = imageUrls[activeIndex];

  return (
    <InstagramStoryContainer
      style={{
        backgroundImage: `url(${imageUrl})`,
      }}
    >
      <Backdrop />
      <Text css={{ color: 'white', marginBottom: '8px' }} textStyle="heading-lg">
        {title}
      </Text>
      <Text css={{ color: 'white', padding: '0 8px', marginBottom: '32px' }} textStyle="body-sm">
        {description}
      </Text>
      <Icon>
        <IconChevronUpFilled color="white" />
      </Icon>
      <LearnMoreButton css={{ marginTop: '8px' }}>Learn more</LearnMoreButton>
      <StoryHeader>
        {imageUrls.map((url, index) => {
          return (
            <StoryLine key={url} active={index === activeIndex} finished={index < activeIndex && activeIndex !== 0} />
          );
        })}
      </StoryHeader>
    </InstagramStoryContainer>
  );
};
